import * as React from "react";
import { produce } from "immer";

import { nullElement } from "../../TypingUtils";
import { AbsoluteModelPath, convertToLegacyPath } from "../../../../../Common/ModelPath/ModelPath";

import { createReduxBasedContext } from "./ReduxBasedContext";

type RowId = string;
type RowHeight = number;
interface RowHeightsCollection {
    [rowIndex: string]: {
        heights: Record<RowId, RowHeight>;
        maxHeight: RowHeight;
    };
}

export interface InstanceToHeightMap {
    [instanceId: string]:
        | {
              rowHeights: RowHeightsCollection;
              height: number;
          }
        | undefined;
}

export interface MultilineInstanceHeightInfo {
    rowHeight: InstanceToHeightMap;
    lastRowHeight?: {
        heights: Record<RowId, RowHeight>;
        maxHeight: RowHeight;
    };
}

export interface MultilineInstanceHeightState {
    rowHeights: {
        [bindingPath: string]: MultilineInstanceHeightInfo | undefined;
    };
}

enum TableActionType {
    UpdateRowHeight = "UpdateRowHeight",
    ClearInstance = "ClearInstance",
    UpdateLastRowHeight = "UpdateLastRowHeight",
}

type MultilineInstanceHeightAction =
    | {
          type: TableActionType.UpdateRowHeight;
          payload: {
              instanceId: string;
              bindingPath: string;
              row: {
                  id: string;
                  index: string;
                  height: number;
              };
          };
      }
    | {
          type: TableActionType.ClearInstance;
          payload: {
              instanceId: string;
              bindingPath: string;
          };
      }
    | {
          type: TableActionType.UpdateLastRowHeight;
          payload: {
              rowId: string;
              rowHeight: number;
              bindingPath: string;
          };
      };

const defaultMultilineInstanceHeightInfo: MultilineInstanceHeightInfo = { rowHeight: {} };

function multilineRowHeightReducer(
    state: MultilineInstanceHeightState,
    action: MultilineInstanceHeightAction
): MultilineInstanceHeightState {
    switch (action.type) {
        case TableActionType.UpdateRowHeight: {
            const { row, bindingPath, instanceId } = action.payload;
            const nextState = produce(state, draft => {
                draft.rowHeights[bindingPath] ??= { rowHeight: {} };
                const instances = draft.rowHeights[bindingPath];
                instances.rowHeight[instanceId] ??= { rowHeights: {}, height: 0 };
                const instance = instances.rowHeight[instanceId];

                const rowIndex = row.index;
                instance.rowHeights[rowIndex] ??= { heights: {}, maxHeight: 0 };
                const rowHeightsCollection = instance.rowHeights[rowIndex];

                rowHeightsCollection.heights[row.id] = row.height;
                rowHeightsCollection.maxHeight = getMaxRowHeight(rowHeightsCollection.heights);
                instance.height = getInstanceHeight(instance.rowHeights);
            });
            return nextState;
        }
        case TableActionType.UpdateLastRowHeight: {
            const { bindingPath, rowId, rowHeight } = action.payload;
            const nextState = produce(state, draft => {
                draft.rowHeights[bindingPath] ??= { rowHeight: {} };
                draft.rowHeights[bindingPath].lastRowHeight ??= {
                    heights: {},
                    maxHeight: 0,
                };
                const instance = draft.rowHeights[bindingPath].lastRowHeight;
                instance.heights[rowId] = rowHeight;
                instance.maxHeight = Math.min(...Object.values(instance.heights)) || 0;
            });
            return nextState;
        }
        case TableActionType.ClearInstance: {
            const { bindingPath, instanceId } = action.payload;
            const nextState = produce(state, draft => {
                const instances = draft.rowHeights[bindingPath];
                const instance = instances && instances.rowHeight[instanceId];
                if (instance) {
                    instances.rowHeight[instanceId] = { rowHeights: {}, height: 0 };
                }
            });
            return nextState;
        }
        default: {
            return state ?? { rowHeights: {} };
        }
    }
}

const MultilineRowHeightContext = createReduxBasedContext(multilineRowHeightReducer);

export function MultilineRowHeightContextRoot(props: { children?: JSX.Element }): JSX.Element {
    return <MultilineRowHeightContext.Root>{props.children}</MultilineRowHeightContext.Root>;
}

interface MultilineRowHeightConsumerProps {
    bindingPath: AbsoluteModelPath;
    children?: (state: MultilineInstanceHeightInfo) => JSX.Element;
}

export function MultilineRowHeightConsumer(props: MultilineRowHeightConsumerProps): JSX.Element {
    return (
        <MultilineRowHeightContext.ConnectToState>
            {(state: MultilineInstanceHeightState) => {
                const rowHeight: undefined | MultilineInstanceHeightInfo =
                    state.rowHeights[props.bindingPath.toLegacyPath()];
                return props.children ? props.children(rowHeight || defaultMultilineInstanceHeightInfo) : nullElement;
            }}
        </MultilineRowHeightContext.ConnectToState>
    );
}

export type ChangeRowHeightHandler = (params: {
    row: {
        id: string;
        index: string;
        height: number;
    };
    instanceId: string;
}) => void;
export type ChangeLastRowHeightHandler = (params: { rowId: string; rowHeight: number }) => void;
export type RowRemovedHandler = (instanceId: string) => void;

interface MultilineRowHeightProviderProps {
    bindingPath: AbsoluteModelPath;
    children?: (params: {
        onHeightChanged: ChangeRowHeightHandler;
        onChangeLastRowHeight: ChangeLastRowHeightHandler;
        onRowRemoved: RowRemovedHandler;
    }) => JSX.Element;
}

export function MultilineRowHeightProvider(props: MultilineRowHeightProviderProps): JSX.Element {
    const bindingPath = convertToLegacyPath(props.bindingPath);
    return (
        <MultilineRowHeightContext.ConnectToActions>
            {dispatch =>
                props.children
                    ? props.children({
                          onHeightChanged: rowParams => {
                              dispatch({
                                  type: TableActionType.UpdateRowHeight,
                                  payload: {
                                      ...rowParams,
                                      bindingPath: bindingPath,
                                  },
                              });
                          },
                          onChangeLastRowHeight: lastRowParams =>
                              dispatch({
                                  type: TableActionType.UpdateLastRowHeight,
                                  payload: {
                                      ...lastRowParams,
                                      bindingPath: bindingPath,
                                  },
                              }),
                          onRowRemoved: instanceId => {
                              dispatch({
                                  type: TableActionType.ClearInstance,
                                  payload: {
                                      instanceId: instanceId,
                                      bindingPath: bindingPath,
                                  },
                              });
                          },
                      })
                    : nullElement
            }
        </MultilineRowHeightContext.ConnectToActions>
    );
}

const getMaxRowHeight = (heights: Record<RowId, RowHeight>) => Math.max(...Object.values(heights)) || 0;
const getInstanceHeight = (rowHeights: RowHeightsCollection): number => {
    const rowMaxHeights = Object.values(rowHeights).map(({ maxHeight }) => maxHeight);
    return rowMaxHeights.reduce((instanceHeight, rowHeight) => instanceHeight + rowHeight, 0);
};
